import { create } from 'zustand';
import { evaluateAlerts, resolveThresholds } from '@shared/index';
import type { AlertEvaluation, AlertLevel } from '@shared/alerts';
import type { Drone, DroneTelemetry } from '@shared/types';
import { telemetryBus } from './bus';
import { DroneSession, initialLinkSnapshot, type LinkSnapshot } from './session';
import { createTelemetryThrottle, type TelemetryThrottle } from './throttle';
import { clearTrail, recordTrailPoint } from './trail';

export interface FleetLinkState {
  droneId: string;
  link: LinkSnapshot;
  telemetry: DroneTelemetry | null;
  alerts: AlertEvaluation | null;
  alertLevel: AlertLevel;
}

interface FleetLinkStore {
  entries: Record<string, FleetLinkState>;
}

/** 只有需要被 React 渲染的遥测才写进 store，限制在 5Hz，避免 20Hz 全树重渲染。 */
const STORE_WRITE_INTERVAL_MS = 200;
/** 失联判定需要「无新帧也要重算」，因此存在一个 1Hz 的兜底重算。 */
const STALE_TICK_MS = 1_000;

export const useFleetLinkStore = create<FleetLinkStore>(() => ({ entries: {} }));

/* ------------------------------------------------------------------ *
 * 会话管理（非响应式，放在 store 之外）
 * ------------------------------------------------------------------ */

const sessions = new Map<string, DroneSession>();
const refCounts = new Map<string, number>();
const dronesById = new Map<string, Drone>();
const telemetryThrottles = new Map<string, TelemetryThrottle>();

let staleTimer: number | null = null;

function ensureEntry(droneId: string): FleetLinkState {
  const existing = useFleetLinkStore.getState().entries[droneId];
  if (existing) return existing;
  return {
    droneId,
    link: initialLinkSnapshot(droneId),
    telemetry: null,
    alerts: null,
    alertLevel: 'nominal',
  };
}

function commit(droneId: string, patch: Partial<FleetLinkState>): void {
  const state = useFleetLinkStore.getState();
  const previous = state.entries[droneId] ?? ensureEntry(droneId);
  useFleetLinkStore.setState({
    entries: { ...state.entries, [droneId]: { ...previous, ...patch } },
  });
}

function evaluateFor(droneId: string, telemetry: DroneTelemetry | null): AlertEvaluation {
  const drone = dronesById.get(droneId);
  return evaluateAlerts({
    telemetry,
    thresholds: resolveThresholds(drone?.thresholds ?? null),
    staleAfterMs: drone?.staleAfterMs ?? 3_000,
    now: Date.now(),
  });
}

function flushTelemetry(droneId: string, telemetry: DroneTelemetry): void {
  const alerts = evaluateFor(droneId, telemetry);
  commit(droneId, { telemetry, alerts, alertLevel: alerts.level });
}

function throttleFor(droneId: string): TelemetryThrottle {
  const existing = telemetryThrottles.get(droneId);
  if (existing) return existing;
  const throttle = createTelemetryThrottle({
    intervalMs: STORE_WRITE_INTERVAL_MS,
    now: () => Date.now(),
    setTimer: (callback, delayMs) => window.setTimeout(callback, delayMs),
    clearTimer: (handle) => window.clearTimeout(handle),
    write: (telemetry) => flushTelemetry(droneId, telemetry),
  });
  telemetryThrottles.set(droneId, throttle);
  return throttle;
}

function onTelemetry(droneId: string, telemetry: DroneTelemetry): void {
  recordTrailPoint(droneId, telemetry.position.lng, telemetry.position.lat);
  // 硬实时视图走总线，全速派发
  telemetryBus.emit(droneId, telemetry);
  throttleFor(droneId).push(telemetry);
}

function startStaleTicker(): void {
  if (staleTimer !== null) return;
  staleTimer = window.setInterval(() => {
    for (const droneId of sessions.keys()) {
      const entry = useFleetLinkStore.getState().entries[droneId];
      if (!entry || !entry.telemetry) continue;
      const alerts = evaluateFor(droneId, entry.telemetry);
      if (alerts.level !== entry.alertLevel || alerts.items.length !== (entry.alerts?.items.length ?? -1)) {
        commit(droneId, { alerts, alertLevel: alerts.level });
      }
    }
  }, STALE_TICK_MS);
}

function stopStaleTickerIfIdle(): void {
  if (sessions.size > 0 || staleTimer === null) return;
  window.clearInterval(staleTimer);
  staleTimer = null;
}

function createSession(drone: Drone): DroneSession {
  const session = new DroneSession(drone, {
    onSnapshot: (patch) => {
      const entry = useFleetLinkStore.getState().entries[drone.id] ?? ensureEntry(drone.id);
      commit(drone.id, { link: { ...entry.link, ...patch } });
    },
    onTelemetry: (telemetry, issues) => {
      onTelemetry(drone.id, telemetry);
      const entry = useFleetLinkStore.getState().entries[drone.id];
      if (entry && issues.length !== entry.link.issues.length) {
        commit(drone.id, { link: { ...entry.link, issues } });
      }
    },
    onError: (error, context) => {
      console.warn(`[havorix] ${context}`, error);
    },
  });
  sessions.set(drone.id, session);
  commit(drone.id, { link: initialLinkSnapshot(drone.id) });
  return session;
}

/**
 * 获取（必要时创建）某台无人机的实时链路，并增加引用计数。
 * 返回释放函数；引用归零时链路自动断开，避免离开页面后仍在后台跑遥测。
 */
export function acquireLink(drone: Drone): () => void {
  dronesById.set(drone.id, drone);
  const refs = refCounts.get(drone.id) ?? 0;
  refCounts.set(drone.id, refs + 1);

  const existing = sessions.get(drone.id);
  if (existing) existing.updateDrone(drone);
  else createSession(drone).start();

  startStaleTicker();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const next = (refCounts.get(drone.id) ?? 1) - 1;
    if (next > 0) {
      refCounts.set(drone.id, next);
      return;
    }
    refCounts.delete(drone.id);
    const session = sessions.get(drone.id);
    session?.stop();
    sessions.delete(drone.id);
    dronesById.delete(drone.id);
    telemetryBus.clear(drone.id);
    clearTrail(drone.id);
    telemetryThrottles.get(drone.id)?.reset();
    telemetryThrottles.delete(drone.id);
    stopStaleTickerIfIdle();
  };
}

/** 注册表变更后同步已有链路的配置（地址、凭据、频率、阈值）。 */
export function syncLinkConfig(drone: Drone): void {
  if (!sessions.has(drone.id)) return;
  dronesById.set(drone.id, drone);
  sessions.get(drone.id)?.updateDrone(drone);
}

export function getSession(droneId: string): DroneSession | undefined {
  return sessions.get(droneId);
}

export function stopAllLinks(): void {
  for (const session of sessions.values()) session.stop();
  sessions.clear();
  refCounts.clear();
  dronesById.clear();
  for (const throttle of telemetryThrottles.values()) throttle.reset();
  telemetryThrottles.clear();
  stopStaleTickerIfIdle();
  useFleetLinkStore.setState({ entries: {} });
}

/** 供测试与登出使用。 */
export function resetFleetLinks(): void {
  stopAllLinks();
  telemetryBus.clear();
  clearTrail();
}
